import type {
  Cell,
  CubeColor,
  CubeHellAction,
  CubeHellPlayerId,
  QueryAxis,
} from "@cloudnest/cube-hell-engine";
import {
  CUBE_COLORS,
  isCell,
  isPlayerId,
  QUERY_AXES,
} from "@cloudnest/cube-hell-engine";

/**
 * Typed replay-event adapter for 큐브지옥 (cube-hell).
 *
 * Hidden-information game: the persisted `init` event carries the full
 * authoritative state (both 3×3×3 arrangements + rng); the replay runner masks
 * it for display. Player events are structured queries (QueryCell/QueryLayer)
 * and the full-arrangement Guess. Malformed events fail loudly.
 */
export type CubeHellReplayEventType =
  | "init"
  | "QueryCell"
  | "QueryLayer"
  | "Guess";

export const CUBE_HELL_REPLAY_EVENT_TYPES = [
  "init",
  "QueryCell",
  "QueryLayer",
  "Guess",
] as const satisfies readonly CubeHellReplayEventType[];

export function normalizeCubeHellReplayEventType(
  eventType: string,
): CubeHellReplayEventType {
  if ((CUBE_HELL_REPLAY_EVENT_TYPES as readonly string[]).includes(eventType)) {
    return eventType as CubeHellReplayEventType;
  }
  throw new Error(`Unsupported cube-hell event type: ${eventType}`);
}

function asPlayerId(value: unknown): CubeHellPlayerId {
  if (isPlayerId(value)) return value;
  throw new Error(`Invalid cube-hell playerId in event payload: ${value}`);
}

function asAxis(value: unknown): QueryAxis {
  if (
    typeof value === "string" &&
    (QUERY_AXES as readonly string[]).includes(value)
  ) {
    return value as QueryAxis;
  }
  throw new Error(`Invalid cube-hell query axis in event payload: ${value}`);
}

function asColor(value: unknown): CubeColor {
  if (
    typeof value === "string" &&
    (CUBE_COLORS as readonly string[]).includes(value)
  ) {
    return value as CubeColor;
  }
  throw new Error(`Invalid cube-hell colour in event payload: ${value}`);
}

function asCoord(value: unknown): number {
  if (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 0 &&
    value < 3
  ) {
    return value;
  }
  throw new Error(`Invalid cube-hell coordinate in event payload: ${value}`);
}

function asArrangement(value: unknown): Cell[] {
  if (!Array.isArray(value) || value.length !== 27) {
    throw new Error("Invalid cube-hell guess arrangement in event payload");
  }
  return value.map((cell) => {
    if (!isCell(cell)) {
      throw new Error(`Invalid cube-hell cell in guess arrangement: ${cell}`);
    }
    return cell;
  });
}

export function cubeHellEventToAction(
  eventType: string,
  payload: Record<string, unknown>,
): CubeHellAction {
  const type = normalizeCubeHellReplayEventType(eventType);
  switch (type) {
    case "init":
      throw new Error("cubeHellEventToAction cannot convert the init event");
    case "QueryCell":
      return {
        type: "QueryCell",
        playerId: asPlayerId(payload.playerId),
        x: asCoord(payload.x),
        y: asCoord(payload.y),
        z: asCoord(payload.z),
      };
    case "QueryLayer":
      return {
        type: "QueryLayer",
        playerId: asPlayerId(payload.playerId),
        axis: asAxis(payload.axis),
        index: asCoord(payload.index),
        color: asColor(payload.color),
      };
    case "Guess":
      return {
        type: "Guess",
        playerId: asPlayerId(payload.playerId),
        arrangement: asArrangement(payload.arrangement),
      };
  }
}
